/**
 * Trigger a browser download for a blob by creating a transient anchor element
 * and clicking it. Revokes the object URL on the next tick.
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Deferred: some browsers cancel a download whose object URL is revoked in
  // the same tick as the click.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Serialize rows to RFC-4180 CSV. `columns` fixes the header order; each entry
 * is `[header, accessor]`.
 *
 * Quoting is load-bearing: gitleaks rule descriptions all contain commas, and
 * an unquoted join silently loses columns in a spreadsheet.
 */
export function toCsv<T>(
  rows: readonly T[],
  columns: readonly (readonly [string, (row: T) => string | number | boolean])[]
): string {
  const escape = (value: string | number | boolean): string => {
    const text = String(value);
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };

  const header = columns.map(([name]) => escape(name)).join(',');
  const body = rows.map(row =>
    columns.map(([, accessor]) => escape(accessor(row))).join(',')
  );
  return [header, ...body].join('\r\n') + '\r\n';
}
